import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogFormGrid, DialogFormSection } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { useToast } from "../hooks/use-toast";
import PayrollScheduleCard from "./payroll/PayrollScheduleCard";
import { useAuth } from "../contexts/AuthContext";
import { buildApiUrl, payrollAPI, employeeAPI, workspaceAdminAPI } from "../services/api";
import { hasAnyRole } from "../lib/rbac";
import {
  Loader2,
  Plus,
  Pencil,
  Eye,
  Trash2,
  Download,
  FileText,
  DollarSign,
  Calculator,
  Receipt,
  Landmark,
  Home,
  Wallet,
  Layers3,
  Percent,
  Settings2,
  PlayCircle,
  ScrollText,
  IndianRupee,
  Banknote,
} from "lucide-react";
import { TableExportActions } from "./ui/table-export-actions";
import { EmptyState } from "./common/States";
import { StatCard } from "./common/StatCard";

const unwrapArrayResponse = (response) => {
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
};

const unwrapObjectResponse = (response) => {
  if (response && typeof response === "object" && "data" in response && response.data && typeof response.data === "object" && !Array.isArray(response.data)) {
    return response.data;
  }
  if (response && typeof response === "object" && !Array.isArray(response)) {
    return response;
  }
  return null;
};

const emptyStructureForm = {
  name: "",
  description: "",
  basic_percent: 40,
  hra_percent: 20,
  da_percent: 0,
  conveyance_allowance: 1600,
  medical_allowance: 1250,
  byod_allowance: 0,
  special_allowance_percent: 0,
  ex_gratia_bonus_annual: 0,
  performance_bonus_annual: 0,
  group_insurance_annual: 0,
  gratuity_annual: 0,
  pf_enabled: true,
  pf_employee_percent: 12,
  pf_employer_percent: 12,
  pf_wage_ceiling: 15000,
  esi_enabled: true,
  esi_employee_percent: 0.75,
  esi_employer_percent: 3.25,
  esi_wage_ceiling: 21000,
  professional_tax_enabled: true,
  is_active: true,
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatCurrency = (value) => `₹ ${Number(value || 0).toLocaleString()}`;

const calculateStructurePreview = (structure, annualCtc) => {
  const ctcAnnual = toNumber(annualCtc, 0);
  const ctcMonthly = ctcAnnual / 12;
  const basic = ctcMonthly * (toNumber(structure.basic_percent) / 100);
  const hra = basic * (toNumber(structure.hra_percent) / 100);
  const da = ctcMonthly * (toNumber(structure.da_percent) / 100);
  const conveyance = toNumber(structure.conveyance_allowance);
  const medical = toNumber(structure.medical_allowance);
  const byod = toNumber(structure.byod_allowance);
  const annualBenefitsMonthly = (
    toNumber(structure.ex_gratia_bonus_annual) +
    toNumber(structure.performance_bonus_annual) +
    toNumber(structure.group_insurance_annual) +
    toNumber(structure.gratuity_annual)
  ) / 12;
  const pfEnabled = Boolean(structure.pf_enabled);
  const pfWage = pfEnabled ? Math.min(basic, toNumber(structure.pf_wage_ceiling, 15000)) : 0;
  const pfEmployee = pfWage * (toNumber(structure.pf_employee_percent) / 100);
  const pfEmployer = pfWage * (toNumber(structure.pf_employer_percent) / 100);
  const provisionalGross = basic + hra + da + conveyance + medical + byod;
  const esiEnabled = Boolean(structure.esi_enabled);
  const esiApplicable = esiEnabled && provisionalGross <= toNumber(structure.esi_wage_ceiling, 21000);
  const esiEmployee = esiApplicable ? provisionalGross * (toNumber(structure.esi_employee_percent) / 100) : 0;
  const esiEmployer = esiApplicable ? provisionalGross * (toNumber(structure.esi_employer_percent) / 100) : 0;
  const special = Math.max(0, ctcMonthly - (provisionalGross + pfEmployer + esiEmployer + annualBenefitsMonthly));
  const gross = provisionalGross + special;
  const professionalTax = structure.professional_tax_enabled && gross > 15000 ? 200 : 0;
  const monthlyDeductions = pfEmployee + esiEmployee + professionalTax;
  const netSalary = gross - monthlyDeductions;
  const annualTotal = (gross + pfEmployer + esiEmployer + annualBenefitsMonthly) * 12;

  return {
    ctcAnnual,
    ctcMonthly,
    basic,
    hra,
    da,
    conveyance,
    medical,
    byod,
    special,
    gross,
    pfEmployee,
    pfEmployer,
    esiEmployee,
    esiEmployer,
    professionalTax,
    annualBenefitsMonthly,
    netSalary,
    annualTotal,
  };
};

const PayrollManagement = ({ currentTab = "overview", hiddenTabs = [], scope = "tenant" }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(currentTab);
  const [loading, setLoading] = useState(false);

  const isGlobalPayrollScope = scope === "global" || Boolean(user?.isSuperadmin);
  const isAdminOrManager = isGlobalPayrollScope || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin', 'manager', 'hr_manager']);
  const hasEmployeeProfile = Boolean(user?.employeeId);
  const showMySalary = activeTab === "my-salary" || (activeTab === "overview" && hasEmployeeProfile);
  const salaryStructureImportPath = isGlobalPayrollScope ? '/superadmin/master/import/salary_structure' : '/admin/import/salary_structure';
  const taxSlabImportPath = isGlobalPayrollScope ? '/superadmin/master/import/tax_slab' : '/admin/import/tax_slab';

  useEffect(() => {
    if (currentTab) {
      setActiveTab(currentTab);
    }
  }, [currentTab]);

  const isTabVisible = (tabId) => !hiddenTabs.includes(tabId);

  // Data States
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [approvingId, setApprovingId] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [taxSlabs, setTaxSlabs] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);

  // Form States
  const [newStructure, setNewStructure] = useState(emptyStructureForm);
  const [editingStructureId, setEditingStructureId] = useState(null);
  const [structurePreviewAnnualCtc, setStructurePreviewAnnualCtc] = useState(725000);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeSalaryConfig, setEmployeeSalaryConfig] = useState({
    ctc_annual: 0, salary_structure_id: "", bank_name: "", bank_account_number: "",
    ifsc_code: "", pan_number: "", tax_regime: "new"
  });
  const [newTaxSlab, setNewTaxSlab] = useState({
    financial_year: "2026-27", regime: "new", min_income: 0, max_income: "", tax_rate: 0, cess: 4
  });
  const [editingTaxSlabId, setEditingTaxSlabId] = useState(null);
  const [viewTaxSlab, setViewTaxSlab] = useState(null);
  const [newReimbursement, setNewReimbursement] = useState({
    employee_id: user?.employeeId || "", claim_type: "travel", amount: "", claim_date: new Date().toISOString().split('T')[0], description: "", receipt_url: ""
  });

  const [processMonth, setProcessMonth] = useState(new Date().getMonth() + 1);
  const [processYear, setProcessYear] = useState(new Date().getFullYear());
  const [showStructureDialog, setShowStructureDialog] = useState(false);
  const [showSalaryDialog, setShowSalaryDialog] = useState(false);
  const [showTaxSlabDialog, setShowTaxSlabDialog] = useState(false);
  const [showReimbursementDialog, setShowReimbursementDialog] = useState(false);
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [expenseFilter, setExpenseFilter] = useState("all");

  const [mySalary, setMySalary] = useState(null);
  const structurePreview = useMemo(
    () => calculateStructurePreview(newStructure, structurePreviewAnnualCtc),
    [newStructure, structurePreviewAnnualCtc]
  );

  const updateStructureField = (field, value, fallback = 0) => {
    setNewStructure(prev => ({
      ...prev,
      [field]: typeof value === "boolean" ? value : toNumber(value, fallback),
    }));
  };

  useEffect(() => {
    fetchInitialData();
    // Initial dashboard bootstrap only needs to run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "salary-structures" && isAdminOrManager) fetchStructures();
    if (activeTab === "employee-salaries" && isAdminOrManager) fetchEmployees();
    if (activeTab === "payroll-process" && isAdminOrManager) fetchPayrolls();
    if (activeTab === "payslips") fetchPayslips();
    if (activeTab === "tax-slabs" && isAdminOrManager) fetchTaxSlabs();
    if (activeTab === "overview" || activeTab === "reimbursements") {
      fetchReimbursements(); 
      if (isAdminOrManager) fetchEmployees(); 
    }
    if (showMySalary && hasEmployeeProfile) fetchMySalary();
    // Tab changes intentionally drive this fetch matrix.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showMySalary, hasEmployeeProfile]);

  useEffect(() => {
    setNewReimbursement(prev => ({
      ...prev,
      employee_id: prev.employee_id || user?.employeeId || ""
    }));
  }, [user?.employeeId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const promises = [];
      if (isAdminOrManager) {
        promises.push(fetchStructures());
        promises.push(fetchPayrolls());
      }
      if (hasEmployeeProfile) {
        promises.push(fetchMySalary());
      }
      await Promise.all(promises);
    } catch (error) {
      console.error("Error fetching initial data", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMySalary = async () => {
    if (!user?.employeeId) return;
    try {
      const response = await payrollAPI.getEmployeeSalary(user.employeeId);
      setMySalary(unwrapObjectResponse(response));
    } catch (error) {
      // toast({ title: "Error", description: "Failed to fetch salary details", variant: "destructive" });
    }
  };

  const fetchStructures = async () => {
    try {
      const response = isGlobalPayrollScope
        ? await workspaceAdminAPI.listGlobalSalaryStructures()
        : await payrollAPI.getSalaryStructures();
      setSalaryStructures(unwrapArrayResponse(response));
    } catch (error) {
      setSalaryStructures([]);
      toast({ title: "Error", description: "Failed to fetch salary structures", variant: "destructive" });
    }
  };

  const fetchTaxSlabs = async () => {
    try {
      const response = isGlobalPayrollScope
        ? await workspaceAdminAPI.listGlobalTaxSlabs()
        : await payrollAPI.getTaxSlabs();
      setTaxSlabs(unwrapArrayResponse(response));
    } catch (error) {
      setTaxSlabs([]);
      toast({ title: "Error", description: "Failed to fetch tax slabs", variant: "destructive" });
    }
  };

  const fetchReimbursements = async () => {
    try {
      const response = await payrollAPI.getReimbursements();
      const reimbursementList = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setReimbursements(reimbursementList);
    } catch (error) {
      setReimbursements([]);
      toast({ title: "Error", description: "Failed to fetch reimbursements", variant: "destructive" });
    }
  };

  const fetchPayrolls = async () => {
    try {
      const response = await payrollAPI.listPayrolls(processYear);
      setPayrolls(unwrapArrayResponse(response));
    } catch (error) {
      setPayrolls([]);
      toast({ title: "Error", description: "Failed to fetch payroll history", variant: "destructive" });
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.list();
      setEmployees(unwrapArrayResponse(response));
    } catch (error) {
      setEmployees([]);
      toast({ title: "Error", description: "Failed to fetch employees", variant: "destructive" });
    }
  };

  const fetchPayslips = async () => {
    try {
      const response = await payrollAPI.listPayslips({ year: processYear, month: processMonth });
      setPayslips(unwrapArrayResponse(response));
    } catch (error) {
      setPayslips([]);
      toast({ title: "Error", description: "Failed to fetch payslips", variant: "destructive" });
    }
  };

  const handleCreateStructure = async () => {
    try {
      if (editingStructureId) {
        if (isGlobalPayrollScope) {
          await workspaceAdminAPI.updateGlobalSalaryStructure(editingStructureId, newStructure);
        } else {
          await payrollAPI.updateSalaryStructure(editingStructureId, newStructure);
        }
      } else if (isGlobalPayrollScope) {
        await workspaceAdminAPI.createGlobalSalaryStructure(newStructure);
      } else {
        await payrollAPI.createSalaryStructure(newStructure);
      }
      toast({
        title: "Success",
        description: editingStructureId
          ? (isGlobalPayrollScope ? "Global salary structure updated" : "Salary structure updated")
          : (isGlobalPayrollScope ? "Global salary structure created" : "Salary structure created")
      });
      setShowStructureDialog(false);
      setEditingStructureId(null);
      setNewStructure(emptyStructureForm);
      fetchStructures();
    } catch (error) {
      toast({
        title: "Error",
        description: error?.response?.data?.detail || (editingStructureId ? "Failed to update structure" : "Failed to create structure"),
        variant: "destructive"
      });
    }
  };

  const openCreateStructureDialog = () => {
    setEditingStructureId(null);
    setNewStructure(emptyStructureForm);
    setStructurePreviewAnnualCtc(725000);
    setShowStructureDialog(true);
  };

  const openEditStructureDialog = (structure) => {
    setEditingStructureId(structure.id);
    setNewStructure({
      ...emptyStructureForm,
      ...structure,
    });
    setStructurePreviewAnnualCtc(Math.round((Number(structure?.name?.includes("12 LPA") ? 1200000 : 725000)) || 725000));
    setShowStructureDialog(true);
  };

  const handleDeleteStructure = async (structure) => {
    if (!window.confirm(`Delete salary structure "${structure.name}"?`)) return;
    try {
      if (isGlobalPayrollScope) {
        await workspaceAdminAPI.deleteGlobalSalaryStructure(structure.id);
      } else {
        await payrollAPI.deleteSalaryStructure(structure.id);
      }
      toast({
        title: "Success",
        description: isGlobalPayrollScope ? "Global salary structure deleted" : "Salary structure deleted"
      });
      fetchStructures();
    } catch (error) {
      toast({
        title: "Error",
        description: error?.response?.data?.detail || "Failed to delete structure",
        variant: "destructive"
      });
    }
  };

  const handleConfigureSalary = async () => {
    try {
      if (!selectedEmployee) return;
      await payrollAPI.configureEmployeeSalary(selectedEmployee, employeeSalaryConfig);
      toast({ title: "Success", description: "Salary configured successfully" });
      setShowSalaryDialog(false);
    } catch (error) {
      toast({ title: "Error", description: "Failed to configure salary", variant: "destructive" });
    }
  };

  const handleApprovePayroll = async (payroll) => {
    setApprovingId(payroll.id);
    try {
      await payrollAPI.approvePayroll(payroll.id);
      toast({ title: "Payroll approved", description: "Payslips can now be emailed." });
      await fetchPayrolls();
    } catch (error) {
      toast({
        title: "Could not approve this payroll",
        description: error?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setApprovingId(null);
    }
  };

  const handleProcessPayroll = async () => {
    setProcessingPayroll(true);
    try {
      const result = await payrollAPI.processPayroll(processMonth, processYear);
      toast({ 
        title: "Payroll Processed", 
        description: `Processed for ${result.employees_processed} employees. Total Net: ${result.total_net}` 
      });
      fetchPayrolls();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to process payroll";
      toast({ title: "Error", description: errorMsg, variant: "destructive" });
    } finally {
      setProcessingPayroll(false);
    }
  };

  const resetTaxSlabForm = () => {
    setEditingTaxSlabId(null);
    setNewTaxSlab({ financial_year: "2026-27", regime: "new", min_income: 0, max_income: "", tax_rate: 0, cess: 4 });
  };

  const openAddTaxSlab = () => {
    resetTaxSlabForm();
    setShowTaxSlabDialog(true);
  };

  const openEditTaxSlab = (slab) => {
    setEditingTaxSlabId(slab.id);
    setNewTaxSlab({
      financial_year: slab.financial_year ?? "",
      regime: slab.regime ?? "new",
      min_income: slab.min_income ?? 0,
      max_income: slab.max_income == null ? "" : slab.max_income,
      tax_rate: slab.tax_rate ?? 0,
      cess: slab.cess ?? 0,
    });
    setShowTaxSlabDialog(true);
  };

  const handleDeleteTaxSlab = async (slab) => {
    const confirmed = window.confirm(`Delete tax slab ${slab.financial_year} (${slab.regime})? This cannot be undone.`);
    if (!confirmed) return;
    try {
      if (isGlobalPayrollScope) {
        await workspaceAdminAPI.deleteGlobalTaxSlab(slab.id);
      } else {
        await payrollAPI.deleteTaxSlab(slab.id);
      }
      toast({ title: "Success", description: isGlobalPayrollScope ? "Global tax slab deleted" : "Tax slab deleted" });
      fetchTaxSlabs();
    } catch (error) {
      toast({ title: "Error", description: error?.response?.data?.detail || "Failed to delete tax slab", variant: "destructive" });
    }
  };

  const handleCreateTaxSlab = async () => {
    try {
      const payload = {
        ...newTaxSlab,
        max_income: newTaxSlab.max_income === "" ? null : newTaxSlab.max_income
      };
      if (editingTaxSlabId) {
        if (isGlobalPayrollScope) {
          await workspaceAdminAPI.updateGlobalTaxSlab(editingTaxSlabId, payload);
        } else {
          await payrollAPI.updateTaxSlab(editingTaxSlabId, payload);
        }
        toast({ title: "Success", description: isGlobalPayrollScope ? "Global tax slab updated" : "Tax slab updated" });
      } else if (isGlobalPayrollScope) {
        await workspaceAdminAPI.createGlobalTaxSlab(payload);
        toast({ title: "Success", description: "Global tax slab added" });
      } else {
        await payrollAPI.createTaxSlab(payload);
        toast({ title: "Success", description: "Tax Slab added" });
      }
      setShowTaxSlabDialog(false);
      resetTaxSlabForm();
      fetchTaxSlabs();
    } catch (error) {
      toast({ title: "Error", description: error?.response?.data?.detail || "Failed to save tax slab", variant: "destructive" });
    }
  };

  const handleCreateReimbursement = async () => {
    try {
      const payload = {
        ...newReimbursement,
        employee_id: newReimbursement.employee_id || user?.employeeId
      };
      await payrollAPI.createReimbursement(payload);
      toast({ title: "Success", description: "Reimbursement claim submitted" });
      setShowReimbursementDialog(false);
      setNewReimbursement({
        employee_id: user?.employeeId || "",
        claim_type: "travel",
        amount: "",
        claim_date: new Date().toISOString().split('T')[0],
        description: "",
        receipt_url: ""
      });
      fetchReimbursements();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create reimbursement claim", variant: "destructive" });
    }
  };

  const handleUpdateReimbursementStatus = async (id, status) => {
    try {
      await payrollAPI.updateReimbursementStatus(id, status);
      toast({
        title: `Claim ${status}`,
        description: `Reimbursement claim has been ${status}.`
      });
      fetchReimbursements();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to update claim status";
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    }
  };

  const reimbursementStats = useMemo(() => {
    const claims = Array.isArray(reimbursements) ? reimbursements : [];
    const pendingClaims = claims.filter(claim => claim.status === "pending");
    const pastClaims = claims.filter(
      claim => claim.status === "approved" || claim.status === "rejected"
    );

    return {
      pendingCount: pendingClaims.length,
      pendingAmount: pendingClaims.reduce((sum, claim) => sum + Number(claim.amount || 0), 0),
      pastCount: pastClaims.length,
      pastAmount: pastClaims.reduce((sum, claim) => sum + Number(claim.amount || 0), 0),
      totalClaims: claims.length,
    };
  }, [reimbursements]);

  const visibleReimbursements = useMemo(() => {
    if (expenseFilter === "pending") {
      return reimbursements.filter(claim => claim.status === "pending");
    }

    if (expenseFilter === "history") {
      return reimbursements.filter(
        claim => claim.status === "approved" || claim.status === "rejected"
      );
    }

    return reimbursements;
  }, [expenseFilter, reimbursements]);

  const openExpenseClaims = (filter = "all", openDialog = false) => {
    setExpenseFilter(filter);
    setActiveTab("reimbursements");
    if (openDialog) {
      setShowReimbursementDialog(true);
    }
  };

  const salaryStructuresExportColumns = useMemo(
    () => [
      { label: "Name", accessor: (row) => row.name },
      { label: "Basic %", accessor: (row) => row.basic_percent },
      { label: "HRA %", accessor: (row) => row.hra_percent },
      { label: "Conveyance", accessor: (row) => row.conveyance_allowance },
      { label: "Medical", accessor: (row) => row.medical_allowance },
      { label: "BYOD", accessor: (row) => row.byod_allowance },
      { label: "Ex-Gratia Annual", accessor: (row) => row.ex_gratia_bonus_annual },
      { label: "Performance Bonus Annual", accessor: (row) => row.performance_bonus_annual },
      { label: "Insurance Annual", accessor: (row) => row.group_insurance_annual },
      { label: "Gratuity Annual", accessor: (row) => row.gratuity_annual },
      { label: "PF Enabled", accessor: (row) => (row.pf_enabled ? "Yes" : "No") },
      { label: "ESI Enabled", accessor: (row) => (row.esi_enabled ? "Yes" : "No") },
    ],
    []
  );

  const taxSlabsExportColumns = useMemo(
    () => [
      { label: "Financial Year", accessor: (row) => row.financial_year },
      { label: "Regime", accessor: (row) => row.regime },
      {
        label: "Income Range",
        accessor: (row) => `${Number(row.min_income || 0).toLocaleString()} - ${row.max_income ? Number(row.max_income).toLocaleString() : "Above"}`,
      },
      { label: "Tax Rate (%)", accessor: (row) => row.tax_rate },
      { label: "Cess (%)", accessor: (row) => row.cess },
    ],
    []
  );

  const reimbursementsExportColumns = useMemo(
    () => [
      { label: "Claim Date", accessor: (row) => row.claim_date },
      { label: "Employee ID", accessor: (row) => row.employee_id },
      { label: "Claim Type", accessor: (row) => row.claim_type },
      { label: "Amount", accessor: (row) => row.amount },
      { label: "Status", accessor: (row) => row.status },
      { label: "Description", accessor: (row) => row.description },
    ],
    []
  );

  const payrollRunsExportColumns = useMemo(
    () => [
      { label: "Month", accessor: (row) => row.month },
      { label: "Year", accessor: (row) => row.year },
      { label: "Status", accessor: (row) => row.status },
      { label: "Employees", accessor: (row) => row.total_employees },
      { label: "Total Net", accessor: (row) => row.total_net },
    ],
    []
  );

  const payslipsExportColumns = useMemo(
    () => [
      { label: "Employee Name", accessor: (row) => row.employee_name },
      { label: "Employee ID", accessor: (row) => row.employee_id },
      { label: "Month", accessor: (row) => row.month },
      { label: "Year", accessor: (row) => row.year },
      { label: "Gross Earnings", accessor: (row) => row.gross_earnings },
      { label: "Total Deductions", accessor: (row) => row.total_deductions },
      { label: "Net Salary", accessor: (row) => row.net_salary },
    ],
    []
  );

  const renderSalaryPanel = () => {
    if (!mySalary) {
      return (
        <Card className="border-dashed border-primary/30 bg-muted/20">
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Landmark className="h-7 w-7" />
            </div>
            <h4 className="text-2xl font-semibold">Looks like Payroll is disabled</h4>
            <p className="mx-auto mt-2 max-w-md text-muted-foreground">
              Your payroll has not been enabled by your organization yet.
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-1">
        <CardHeader>
          <CardTitle>My Salary Structure</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Annual CTC</p>
                <p className="text-3xl font-bold">
                  {mySalary?.ctc_annual ? `₹ ${mySalary.ctc_annual.toLocaleString()}` : "₹ --"}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Monthly Gross</p>
                <p className="text-3xl font-bold">
                  {mySalary?.ctc_monthly ? `₹ ${mySalary.ctc_monthly.toLocaleString()}` : "₹ --"}
                </p>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/20 p-4">
              <h4 className="mb-4 font-semibold">Salary Breakdown (Monthly)</h4>
              <div className="grid grid-cols-1 gap-x-8 gap-y-2 md:grid-cols-2">
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">Basic Salary</span>
                  <span className="font-medium">{mySalary?.basic_salary !== undefined ? `₹ ${mySalary.basic_salary.toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">HRA</span>
                  <span className="font-medium">{mySalary?.hra !== undefined ? `₹ ${mySalary.hra.toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">Dearness Allowance</span>
                  <span className="font-medium">{mySalary?.dearness_allowance !== undefined ? `₹ ${mySalary.dearness_allowance.toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">Conveyance Allowance</span>
                  <span className="font-medium">{mySalary?.conveyance_allowance !== undefined ? `₹ ${mySalary.conveyance_allowance.toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">Medical Allowance</span>
                  <span className="font-medium">{mySalary?.medical_allowance !== undefined ? `₹ ${mySalary.medical_allowance.toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">BYOD Allowance</span>
                  <span className="font-medium">{mySalary?.byod_allowance !== undefined ? `₹ ${mySalary.byod_allowance.toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">Special Allowance</span>
                  <span className="font-medium">{mySalary?.special_allowance !== undefined ? `₹ ${mySalary.special_allowance.toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 bg-muted/30 px-2 py-1 font-semibold">
                  <span className="font-semibold text-muted-foreground">Gross Salary</span>
                  <span className="font-medium">{mySalary?.gross_salary !== undefined ? `₹ ${mySalary.gross_salary.toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">PF (Employee)</span>
                  <span className="font-medium text-destructive">{mySalary?.pf_employee ? `- ₹ ${mySalary.pf_employee.toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">Professional Tax</span>
                  <span className="font-medium text-destructive">{mySalary?.professional_tax ? `- ₹ ${mySalary.professional_tax.toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">ESI (Employee)</span>
                  <span className="font-medium text-destructive">{mySalary?.esi_employee ? `- ₹ ${mySalary.esi_employee.toLocaleString()}` : "--"}</span>
                </div>
                <div className="mt-2 flex justify-between rounded border-b border-border/50 bg-[hsl(var(--chart-2))]/10 px-2 py-2">
                  <span className="text-lg font-bold text-muted-foreground">Net Salary (Approx)</span>
                  <span className="text-lg font-bold text-[hsl(var(--chart-2))]">{mySalary?.net_salary ? `₹ ${mySalary.net_salary.toLocaleString()}` : "--"}</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/20 p-4">
              <h4 className="mb-4 font-semibold">Annual Employer Components</h4>
              <div className="grid grid-cols-1 gap-x-8 gap-y-2 md:grid-cols-2">
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">Ex-Gratia / Festival Bonus</span>
                  <span className="font-medium">{mySalary?.ex_gratia_bonus_annual !== undefined ? `₹ ${Number(mySalary.ex_gratia_bonus_annual || 0).toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">Performance Bonus</span>
                  <span className="font-medium">{mySalary?.performance_bonus_annual !== undefined ? `₹ ${Number(mySalary.performance_bonus_annual || 0).toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">Group Health / Life Insurance</span>
                  <span className="font-medium">{mySalary?.group_insurance_annual !== undefined ? `₹ ${Number(mySalary.group_insurance_annual || 0).toLocaleString()}` : "--"}</span>
                </div>
                <div className="flex justify-between rounded border-b border-border/50 px-2 py-1 transition-colors hover:bg-muted/50">
                  <span className="text-muted-foreground">Gratuity</span>
                  <span className="font-medium">{mySalary?.gratuity_annual !== undefined ? `₹ ${Number(mySalary.gratuity_annual || 0).toLocaleString()}` : "--"}</span>
                </div>
              </div>
            </div>

            {mySalary?.effective_from && (
              <p className="text-right text-xs text-muted-foreground">
                Effective from: {new Date(mySalary.effective_from).toLocaleDateString()}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-hr-h2 font-bold tracking-tight">Finance Center</h2>
          <p className="text-hr-sm text-muted-foreground">Manage salary, expenses, payroll cycles, and payslips.</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="sticky top-2 z-20 border border-border bg-background">
          {isTabVisible("overview") && <TabsTrigger value="overview" className="text-hr-sm gap-1.5"><Home className="h-3.5 w-3.5" />Finance Home</TabsTrigger>}
          {hasEmployeeProfile && isTabVisible("my-salary") && <TabsTrigger value="my-salary" className="text-hr-sm gap-1.5"><Wallet className="h-3.5 w-3.5" />Salary</TabsTrigger>}
          {isAdminOrManager && isTabVisible("salary-structures") && <TabsTrigger value="salary-structures" className="text-hr-sm gap-1.5"><Layers3 className="h-3.5 w-3.5" />Salary Structures</TabsTrigger>}
          {isAdminOrManager && isTabVisible("tax-slabs") && <TabsTrigger value="tax-slabs" className="text-hr-sm gap-1.5"><Percent className="h-3.5 w-3.5" />Tax Slabs</TabsTrigger>}
          {isTabVisible("reimbursements") && <TabsTrigger value="reimbursements" className="text-hr-sm gap-1.5"><Receipt className="h-3.5 w-3.5" />Expenses</TabsTrigger>}
          {isAdminOrManager && isTabVisible("employee-salaries") && <TabsTrigger value="employee-salaries" className="text-hr-sm gap-1.5"><Settings2 className="h-3.5 w-3.5" />Salary Config</TabsTrigger>}
          {isAdminOrManager && isTabVisible("payroll-process") && <TabsTrigger value="payroll-process" className="text-hr-sm gap-1.5"><PlayCircle className="h-3.5 w-3.5" />Run Payroll</TabsTrigger>}
          {isTabVisible("payslips") && <TabsTrigger value="payslips" className="text-hr-sm gap-1.5"><ScrollText className="h-3.5 w-3.5" />Payslips</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="space-y-4 animate-slide-up">
          {hasEmployeeProfile && (
            <div className="space-y-4">
              <div>
                <h3 className="text-hr-lg font-semibold">Salary</h3>
                <p className="text-hr-sm text-muted-foreground">View your current pay details and salary breakdown.</p>
              </div>
              {renderSalaryPanel()}
            </div>
          )}

          <div className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-hr-lg font-semibold">Expenses</h3>
                <p className="text-hr-sm text-muted-foreground">Create claims, review pending expenses, and track past reimbursements.</p>
              </div>
              <Button onClick={() => openExpenseClaims("all", true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add/Claim Expenses
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Card className="card-interactive cursor-pointer border-primary/20 bg-primary/5 transition-all duration-200 hover:-translate-y-1 hover:shadow-md" onClick={() => openExpenseClaims("all", true)}>
                <CardHeader className="space-y-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Receipt className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-hr-lg">Add/Claim Expenses</CardTitle>
                    <CardDescription>Create expenses, advance requests, mileage tracking, per diem and claim them.</CardDescription>
                  </div>
                </CardHeader>
              </Card>

              <Card className="card-interactive cursor-pointer border-[hsl(var(--chart-4))]/20 bg-[hsl(var(--chart-4))]/10 transition-all duration-200 hover:-translate-y-1 hover:shadow-md" onClick={() => openExpenseClaims("pending")}>
                <CardHeader className="space-y-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--chart-4))]/10 text-[hsl(var(--chart-4))]">
                    <Calculator className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-hr-lg">Pending</CardTitle>
                    <CardDescription>Expenses and payments awaiting approval.</CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="text-hr-h2 font-bold">{reimbursementStats.pendingCount}</div>
                  <p className="text-hr-sm text-muted-foreground">₹ {reimbursementStats.pendingAmount.toLocaleString()} awaiting action</p>
                </CardContent>
              </Card>

              <Card className="card-interactive cursor-pointer border-[hsl(var(--chart-2))]/20 bg-[hsl(var(--chart-2))]/10 transition-all duration-200 hover:-translate-y-1 hover:shadow-md" onClick={() => openExpenseClaims("history")}>
                <CardHeader className="space-y-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))]">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-hr-lg">Past Claims</CardTitle>
                    <CardDescription>All claims that have been settled or rejected.</CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="text-hr-h2 font-bold">{reimbursementStats.pastCount}</div>
                  <p className="text-hr-sm text-muted-foreground">₹ {reimbursementStats.pastAmount.toLocaleString()} processed so far</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {isAdminOrManager && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Payroll Runs" value={payrolls.length} icon={Landmark} tone="primary" />
              <StatCard label="Salary Structures" value={salaryStructures.length} icon={DollarSign} tone="success" />
              <StatCard label="Pending Claims" value={reimbursementStats.pendingCount} icon={Receipt} tone="warning" />
              <StatCard label="Total Claims" value={reimbursementStats.totalClaims} icon={FileText} tone="info" />
            </div>
          )}
        </TabsContent>

        <TabsContent value="my-salary" className="space-y-4 animate-slide-up">
          {renderSalaryPanel()}
        </TabsContent>

        <TabsContent value="salary-structures" className="space-y-4 animate-slide-up">
          {isGlobalPayrollScope ? (
            <Card className="border-dashed bg-muted/20">
              <CardContent className="pt-6">
                <p className="text-sm font-medium">Global Salary Structures</p>
                <p className="text-sm text-muted-foreground">
                  These templates seed the platform defaults. Individual tenants can still maintain their own salary structures locally.
                </p>
              </CardContent>
            </Card>
          ) : null}
          <div className="flex justify-between">
            <h3 className="text-lg font-medium">Salary Structures</h3>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate(salaryStructureImportPath)}>
                Import Structures
              </Button>
              <Dialog open={showStructureDialog} onOpenChange={setShowStructureDialog}>
                <DialogTrigger asChild>
                  <Button onClick={openCreateStructureDialog}><Plus className="mr-2 h-4 w-4" /> Create Structure</Button>
                </DialogTrigger>
              <DialogContent size="lg">
                <DialogHeader>
                  <DialogTitle>{editingStructureId ? "Edit Salary Structure" : "Create Salary Structure"}</DialogTitle>
                </DialogHeader>
                <DialogFormSection className="space-y-4 py-4">
                  <DialogFormGrid>
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input value={newStructure.name} onChange={e => setNewStructure({...newStructure, name: e.target.value})} placeholder="e.g. Standard" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input value={newStructure.description} onChange={e => setNewStructure({...newStructure, description: e.target.value})} />
                    </div>
                  </DialogFormGrid>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Basic %</Label>
                      <Input type="number" value={newStructure.basic_percent} onChange={e => updateStructureField("basic_percent", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>HRA %</Label>
                      <Input type="number" value={newStructure.hra_percent} onChange={e => updateStructureField("hra_percent", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>DA %</Label>
                      <Input type="number" value={newStructure.da_percent} onChange={e => updateStructureField("da_percent", e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Conveyance</Label>
                      <Input type="number" value={newStructure.conveyance_allowance} onChange={e => updateStructureField("conveyance_allowance", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Medical</Label>
                      <Input type="number" value={newStructure.medical_allowance} onChange={e => updateStructureField("medical_allowance", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>BYOD</Label>
                      <Input type="number" value={newStructure.byod_allowance} onChange={e => updateStructureField("byod_allowance", e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>PF Employee %</Label>
                      <Input type="number" value={newStructure.pf_employee_percent} onChange={e => updateStructureField("pf_employee_percent", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>PF Employer %</Label>
                      <Input type="number" value={newStructure.pf_employer_percent} onChange={e => updateStructureField("pf_employer_percent", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>PF Wage Ceiling</Label>
                      <Input type="number" value={newStructure.pf_wage_ceiling} onChange={e => updateStructureField("pf_wage_ceiling", e.target.value, 15000)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>ESI Employee %</Label>
                      <Input type="number" value={newStructure.esi_employee_percent} onChange={e => updateStructureField("esi_employee_percent", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>ESI Employer %</Label>
                      <Input type="number" value={newStructure.esi_employer_percent} onChange={e => updateStructureField("esi_employer_percent", e.target.value, 3.25)} />
                    </div>
                    <div className="space-y-2">
                      <Label>ESI Wage Ceiling</Label>
                      <Input type="number" value={newStructure.esi_wage_ceiling} onChange={e => updateStructureField("esi_wage_ceiling", e.target.value, 21000)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ex-Gratia / Festival Bonus Annual</Label>
                      <Input type="number" value={newStructure.ex_gratia_bonus_annual} onChange={e => updateStructureField("ex_gratia_bonus_annual", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Performance Bonus Annual</Label>
                      <Input type="number" value={newStructure.performance_bonus_annual} onChange={e => updateStructureField("performance_bonus_annual", e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Group Insurance Annual</Label>
                      <Input type="number" value={newStructure.group_insurance_annual} onChange={e => updateStructureField("group_insurance_annual", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Gratuity Annual</Label>
                      <Input type="number" value={newStructure.gratuity_annual} onChange={e => updateStructureField("gratuity_annual", e.target.value)} />
                    </div>
                  </div>
                  <div className="rounded-lg border bg-muted/20 p-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <Label>Preview Annual CTC</Label>
                        <Input type="number" value={structurePreviewAnnualCtc} onChange={e => setStructurePreviewAnnualCtc(toNumber(e.target.value, 0))} />
                      </div>
                      <div className="rounded-md border bg-background p-3">
                        <p className="text-xs text-muted-foreground">Monthly Gross</p>
                        <p className="text-lg font-semibold">{formatCurrency(structurePreview.gross)}</p>
                      </div>
                      <div className="rounded-md border bg-background p-3">
                        <p className="text-xs text-muted-foreground">Annual CTC</p>
                        <p className="text-lg font-semibold">{formatCurrency(structurePreview.annualTotal)}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">
                      Formula: CTC = Gross Salary + Employer PF + Employer ESI + Annual Benefits.
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                      <div className="rounded-md border bg-background p-3"><p className="text-xs text-muted-foreground">Basic</p><p className="font-medium">{formatCurrency(structurePreview.basic)}</p></div>
                      <div className="rounded-md border bg-background p-3"><p className="text-xs text-muted-foreground">HRA</p><p className="font-medium">{formatCurrency(structurePreview.hra)}</p></div>
                      <div className="rounded-md border bg-background p-3"><p className="text-xs text-muted-foreground">Special</p><p className="font-medium">{formatCurrency(structurePreview.special)}</p></div>
                      <div className="rounded-md border bg-background p-3"><p className="text-xs text-muted-foreground">Employer PF</p><p className="font-medium">{formatCurrency(structurePreview.pfEmployer)}</p></div>
                      <div className="rounded-md border bg-background p-3"><p className="text-xs text-muted-foreground">Employer ESI</p><p className="font-medium">{formatCurrency(structurePreview.esiEmployer)}</p></div>
                      <div className="rounded-md border bg-background p-3"><p className="text-xs text-muted-foreground">Annual Benefits / Month</p><p className="font-medium">{formatCurrency(structurePreview.annualBenefitsMonthly)}</p></div>
                      <div className="rounded-md border bg-background p-3"><p className="text-xs text-muted-foreground">Professional Tax</p><p className="font-medium">{formatCurrency(structurePreview.professionalTax)}</p></div>
                      <div className="rounded-md border bg-background p-3"><p className="text-xs text-muted-foreground">Net Salary</p><p className="font-medium">{formatCurrency(structurePreview.netSalary)}</p></div>
                    </div>
                  </div>
                </DialogFormSection>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setShowStructureDialog(false); setEditingStructureId(null); setNewStructure(emptyStructureForm); }}>Cancel</Button>
                  <Button onClick={handleCreateStructure}>{editingStructureId ? "Update" : "Create"}</Button>
                </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="flex justify-end">
            <TableExportActions
              rows={salaryStructures}
              columns={salaryStructuresExportColumns}
              fileName="salary_structures"
              title="Salary Structures"
            />
          </div>
          <Table className="text-hr-sm">
            <TableHeader>
              <TableRow>
                <TableHead className="text-hr-sm text-muted-foreground">Name</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Basic %</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">HRA %</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Conveyance</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">BYOD</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Ex-Gratia</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Insurance</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Gratuity</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">PF Enabled</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">ESI Enabled</TableHead>
                <TableHead className="text-right text-hr-sm text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {salaryStructures.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="p-0">
                    <EmptyState
                      icon={Calculator}
                      title="No salary structures yet"
                      description="Create a salary structure to define how CTC breaks down into components."
                    />
                  </TableCell>
                </TableRow>
              )}
              {salaryStructures.map((structure) => (
                <TableRow key={structure.id}>
                  <TableCell className="font-medium text-hr-sm">{structure.name}</TableCell>
                  <TableCell className="text-hr-sm">{structure.basic_percent}%</TableCell>
                  <TableCell className="text-hr-sm">{structure.hra_percent}%</TableCell>
                  <TableCell className="text-hr-sm">₹ {Number(structure.conveyance_allowance || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-hr-sm">₹ {Number(structure.byod_allowance || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-hr-sm">₹ {Number(structure.ex_gratia_bonus_annual || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-hr-sm">₹ {Number(structure.group_insurance_annual || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-hr-sm">₹ {Number(structure.gratuity_annual || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-hr-sm">{structure.pf_enabled ? "Yes" : "No"}</TableCell>
                  <TableCell className="text-hr-sm">{structure.esi_enabled ? "Yes" : "No"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditStructureDialog(structure)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteStructure(structure)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="tax-slabs" className="space-y-4">
          {isGlobalPayrollScope ? (
            <Card className="border-dashed bg-muted/20">
              <CardContent className="pt-6">
                <p className="text-sm font-medium">Global Tax Slabs</p>
                <p className="text-sm text-muted-foreground">
                  These tax slabs seed the platform defaults. Individual tenants can still configure their own local tax slabs as needed.
                </p>
              </CardContent>
            </Card>
          ) : null}
          <div className="flex justify-between">
            <h3 className="text-lg font-medium">Income Tax Slabs</h3>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate(taxSlabImportPath)}>
                Import Tax Slabs
              </Button>
              <Dialog open={showTaxSlabDialog} onOpenChange={(o) => { setShowTaxSlabDialog(o); if (!o) resetTaxSlabForm(); }}>
                <DialogTrigger asChild>
                  <Button onClick={openAddTaxSlab}><Plus className="mr-2 h-4 w-4" /> Add Tax Slab</Button>
                </DialogTrigger>
              <DialogContent size="md">
                <DialogHeader>
                  <DialogTitle>{editingTaxSlabId ? 'Edit Tax Slab' : 'Add Tax Slab'}</DialogTitle>
                </DialogHeader>
                <DialogFormSection className="space-y-4 py-4">
                  <DialogFormGrid>
                     <div className="space-y-2">
                      <Label>Financial Year</Label>
                      <Input value={newTaxSlab.financial_year} onChange={e => setNewTaxSlab({...newTaxSlab, financial_year: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Regime</Label>
                      <Select onValueChange={val => setNewTaxSlab({...newTaxSlab, regime: val})} defaultValue={newTaxSlab.regime}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Regime" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="old">Old</SelectItem>
                          <SelectItem value="new">New</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </DialogFormGrid>
                  <DialogFormGrid>
                    <div className="space-y-2">
                      <Label>Min Income</Label>
                      <Input type="number" value={newTaxSlab.min_income} onChange={e => setNewTaxSlab({...newTaxSlab, min_income: parseFloat(e.target.value)})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Income</Label>
                      <Input type="number" placeholder="Leave blank for infinity" value={newTaxSlab.max_income} onChange={e => setNewTaxSlab({...newTaxSlab, max_income: e.target.value ? parseFloat(e.target.value) : ""})} />
                    </div>
                  </DialogFormGrid>
                  <DialogFormGrid>
                     <div className="space-y-2">
                      <Label>Tax Rate (%)</Label>
                      <Input type="number" value={newTaxSlab.tax_rate} onChange={e => setNewTaxSlab({...newTaxSlab, tax_rate: parseFloat(e.target.value)})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Cess (%)</Label>
                      <Input type="number" value={newTaxSlab.cess} onChange={e => setNewTaxSlab({...newTaxSlab, cess: parseFloat(e.target.value)})} />
                    </div>
                  </DialogFormGrid>
                </DialogFormSection>
                <DialogFooter>
                  <Button onClick={handleCreateTaxSlab}>{editingTaxSlabId ? 'Update Slab' : 'Save Slab'}</Button>
                </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="flex justify-end">
            <TableExportActions
              rows={taxSlabs}
              columns={taxSlabsExportColumns}
              fileName="tax_slabs"
              title="Income Tax Slabs"
            />
          </div>
          <Table className="text-hr-sm">
            <TableHeader>
              <TableRow>
                <TableHead className="text-hr-sm text-muted-foreground">Year</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Regime</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Income Range</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Tax Rate</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Cess</TableHead>
                {isGlobalPayrollScope && <TableHead className="text-hr-sm text-muted-foreground text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {taxSlabs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isGlobalPayrollScope ? 6 : 5} className="p-0">
                    <EmptyState
                      icon={IndianRupee}
                      title="No tax slabs configured"
                      description="Add income tax slabs to drive payroll tax calculations."
                    />
                  </TableCell>
                </TableRow>
              )}
              {taxSlabs.map((slab) => (
                <TableRow key={slab.id}>
                  <TableCell className="text-hr-sm">{slab.financial_year}</TableCell>
                  <TableCell className="capitalize text-hr-sm">{slab.regime}</TableCell>
                  <TableCell className="text-hr-sm">{slab.min_income.toLocaleString()} - {slab.max_income ? slab.max_income.toLocaleString() : "Above"}</TableCell>
                  <TableCell className="text-hr-sm">{slab.tax_rate}%</TableCell>
                  <TableCell className="text-hr-sm">{slab.cess}%</TableCell>
                  {isGlobalPayrollScope && (
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="icon" title="View" onClick={() => setViewTaxSlab(slab)}><Eye className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => openEditTaxSlab(slab)}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Delete" onClick={() => handleDeleteTaxSlab(slab)}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* View tax slab (read-only) */}
          <Dialog open={!!viewTaxSlab} onOpenChange={(o) => !o && setViewTaxSlab(null)}>
            <DialogContent size="md">
              <DialogHeader>
                <DialogTitle>Tax Slab Details</DialogTitle>
              </DialogHeader>
              {viewTaxSlab && (
                <div className="space-y-2 py-2 text-hr-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Financial Year</span><span className="font-medium">{viewTaxSlab.financial_year}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Regime</span><span className="capitalize">{viewTaxSlab.regime}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Income Range</span><span>{viewTaxSlab.min_income?.toLocaleString()} - {viewTaxSlab.max_income ? viewTaxSlab.max_income.toLocaleString() : "Above"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tax Rate</span><span>{viewTaxSlab.tax_rate}%</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Cess</span><span>{viewTaxSlab.cess}%</span></div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="reimbursements" className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-medium">Expense Claims</h3>
              <p className="text-sm text-muted-foreground">Track expense claims, reimbursement approvals, and payment history.</p>
            </div>
            <Dialog open={showReimbursementDialog} onOpenChange={setShowReimbursementDialog}>
              <DialogTrigger asChild>
                <Button><Plus className="mr-2 h-4 w-4" /> New Claim</Button>
              </DialogTrigger>
              <DialogContent size="md">
                <DialogHeader>
                  <DialogTitle>Submit Reimbursement Claim</DialogTitle>
                </DialogHeader>
                <DialogFormSection className="space-y-4 py-4">
                  {isAdminOrManager ? (
                    <div className="space-y-2">
                      <Label>Employee</Label>
                      <Select
                        onValueChange={val => setNewReimbursement({...newReimbursement, employee_id: val})}
                        value={newReimbursement.employee_id || user?.employeeId || ""}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Employee" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map(emp => (
                            <SelectItem key={emp.employee_id} value={emp.employee_id}>
                              {emp.first_name} {emp.last_name} ({emp.employee_id})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Employee</Label>
                      <Input value={user?.employeeId || ""} disabled />
                    </div>
                  )}
                  <DialogFormGrid>
                    <div className="space-y-2">
                      <Label>Claim Type</Label>
                      <Select onValueChange={val => setNewReimbursement({...newReimbursement, claim_type: val})} defaultValue={newReimbursement.claim_type}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="travel">Travel</SelectItem>
                          <SelectItem value="medical">Medical</SelectItem>
                          <SelectItem value="internet">Internet/Phone</SelectItem>
                          <SelectItem value="food">Food/Meal</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input type="number" value={newReimbursement.amount} onChange={e => setNewReimbursement({...newReimbursement, amount: parseFloat(e.target.value)})} />
                    </div>
                  </DialogFormGrid>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input type="date" value={newReimbursement.claim_date} onChange={e => setNewReimbursement({...newReimbursement, claim_date: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input value={newReimbursement.description} onChange={e => setNewReimbursement({...newReimbursement, description: e.target.value})} />
                  </div>
                   <div className="space-y-2">
                    <Label>Receipt URL</Label>
                    <Input value={newReimbursement.receipt_url} onChange={e => setNewReimbursement({...newReimbursement, receipt_url: e.target.value})} placeholder="http://..." />
                  </div>
                </DialogFormSection>
                <DialogFooter>
                  <Button onClick={handleCreateReimbursement}>Submit Claim</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <StatCard label="All Claims" value={reimbursementStats.totalClaims} icon={Receipt} tone="info" hint="Submitted expense entries" />
            <StatCard label="Pending Approval" value={reimbursementStats.pendingCount} icon={Calculator} tone="warning" hint={`₹ ${reimbursementStats.pendingAmount.toLocaleString()} awaiting review`} />
            <StatCard label="Past Claims" value={reimbursementStats.pastCount} icon={FileText} tone="success" hint="Approved or rejected claims" />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant={expenseFilter === "all" ? "default" : "outline"} size="sm" onClick={() => setExpenseFilter("all")}>All</Button>
            <Button variant={expenseFilter === "pending" ? "default" : "outline"} size="sm" onClick={() => setExpenseFilter("pending")}>Pending</Button>
            <Button variant={expenseFilter === "history" ? "default" : "outline"} size="sm" onClick={() => setExpenseFilter("history")}>Past Claims</Button>
          </div>

          <div className="flex justify-end">
            <TableExportActions
              rows={visibleReimbursements}
              columns={reimbursementsExportColumns}
              fileName="reimbursement_claims"
              title="Reimbursement Claims"
            />
          </div>

          <Table className="text-hr-sm">
            <TableHeader>
              <TableRow>
                <TableHead className="text-hr-sm text-muted-foreground">Date</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Employee</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Type</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Amount</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Status</TableHead>
                <TableHead className="text-hr-sm text-muted-foreground">Description</TableHead>
                {isAdminOrManager && <TableHead className="text-hr-sm text-muted-foreground">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleReimbursements.length ? visibleReimbursements.map((claim) => (
                <TableRow key={claim.id}>
                  <TableCell className="text-hr-sm">{new Date(claim.claim_date).toLocaleDateString()}</TableCell>
                  <TableCell className="text-hr-sm">{claim.employee_id}</TableCell>
                  <TableCell className="capitalize text-hr-sm">{claim.claim_type}</TableCell>
                  <TableCell className="text-hr-sm">₹ {Number(claim.amount || 0).toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant={claim.status === "approved" ? "success" : claim.status === "rejected" ? "destructive" : "secondary"}>
                      {claim.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-hr-sm">{claim.description}</TableCell>
                  {isAdminOrManager && (
                    <TableCell>
                      {claim.status === "pending" && (
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" className="text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))] hover:bg-[hsl(var(--chart-2))]/10 hover:text-[hsl(var(--chart-2))]/80 dark:text-[hsl(var(--chart-2))] dark:border-[hsl(var(--chart-2))]/50 dark:hover:bg-[hsl(var(--chart-2))]/20" onClick={() => handleUpdateReimbursementStatus(claim.id, "approved")}>
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" className="text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => handleUpdateReimbursementStatus(claim.id, "rejected")}>
                            Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={isAdminOrManager ? 7 : 6} className="p-0">
                    <EmptyState
                      icon={Receipt}
                      title="No expense claims found"
                      description="No claims match this filter yet. New claims will appear here."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="employee-salaries" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Employee Salary Configuration</h3>
            <Dialog open={showSalaryDialog} onOpenChange={setShowSalaryDialog}>
              <DialogTrigger asChild>
                <Button disabled={!selectedEmployee}><DollarSign className="mr-2 h-4 w-4" /> Configure Salary</Button>
              </DialogTrigger>
              <DialogContent size="md">
                <DialogHeader>
                  <DialogTitle>Configure Salary</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label>Annual CTC</Label>
                    <Input type="number" value={employeeSalaryConfig.ctc_annual} onChange={e => setEmployeeSalaryConfig({...employeeSalaryConfig, ctc_annual: parseFloat(e.target.value)})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Salary Structure</Label>
                    <Select onValueChange={val => setEmployeeSalaryConfig({...employeeSalaryConfig, salary_structure_id: parseInt(val)})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Structure" />
                      </SelectTrigger>
                      <SelectContent>
                        {salaryStructures.map(s => (
                          <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Bank Account Number</Label>
                    <Input value={employeeSalaryConfig.bank_account_number} onChange={e => setEmployeeSalaryConfig({...employeeSalaryConfig, bank_account_number: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>PAN Number</Label>
                    <Input value={employeeSalaryConfig.pan_number} onChange={e => setEmployeeSalaryConfig({...employeeSalaryConfig, pan_number: e.target.value})} />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleConfigureSalary}>Save Configuration</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="border rounded-md p-4">
             <Label className="mb-2 block">Select Employee to View/Edit</Label>
             <Select onValueChange={setSelectedEmployee}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Select Employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_id})
                    </SelectItem>
                  ))}
                </SelectContent>
             </Select>
          </div>
        </TabsContent>

        <TabsContent value="payroll-process" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Process Payroll</CardTitle>
              <CardDescription>Select month and year to process payroll for all active employees.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end">
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select value={processMonth.toString()} onValueChange={v => setProcessMonth(parseInt(v))}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                        <SelectItem key={m} value={m.toString()}>{new Date(0, m-1).toLocaleString('default', { month: 'long' })}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Input type="number" className="w-[120px]" value={processYear} onChange={e => setProcessYear(parseInt(e.target.value))} />
                </div>
                <Button onClick={handleProcessPayroll} disabled={processingPayroll}>
                  {processingPayroll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calculator className="mr-2 h-4 w-4" />}
                  Process Payroll
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <h3 className="text-lg font-medium">Payroll History</h3>
            <div className="mb-3 flex justify-end">
              <TableExportActions
                rows={payrolls}
                columns={payrollRunsExportColumns}
                fileName="payroll_runs"
                title="Payroll Runs"
              />
            </div>
            <Table className="text-hr-sm">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-hr-sm text-muted-foreground">Month/Year</TableHead>
                  <TableHead className="text-hr-sm text-muted-foreground">Status</TableHead>
                  <TableHead className="text-hr-sm text-muted-foreground">Employees</TableHead>
                  <TableHead className="text-hr-sm text-muted-foreground">Total Net Pay</TableHead>
                  <TableHead className="text-hr-sm text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payrolls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="p-0">
                      <EmptyState
                        icon={Banknote}
                        title="No payroll runs yet"
                        description="Process payroll for a month to see the run history here."
                      />
                    </TableCell>
                  </TableRow>
                )}
                {payrolls.map((payroll) => (
                  <TableRow key={payroll.id}>
                    <TableCell className="text-hr-sm">{new Date(0, payroll.month-1).toLocaleString('default', { month: 'long' })} {payroll.year}</TableCell>
                    <TableCell>
                      <Badge variant={payroll.status === "processed" ? "success" : "secondary"}>
                        {payroll.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-hr-sm">{payroll.total_employees}</TableCell>
                    <TableCell className="text-hr-sm">{payroll.total_net.toLocaleString()}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {payroll.status === "processed" && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mr-1"
                          disabled={approvingId === payroll.id}
                          onClick={() => handleApprovePayroll(payroll)}
                        >
                          {approvingId === payroll.id ? "Approving…" : "Approve"}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm">View Details</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="mt-2 text-xs text-muted-foreground">
              Approving freezes a run: it can no longer be reprocessed, which is what
              makes it safe to email the payslips. Whoever processed a run cannot approve it.
            </p>
          </div>

          <PayrollScheduleCard />
        </TabsContent>
        
        <TabsContent value="payslips" className="space-y-4">
            <div className="flex gap-4 mb-4">
                 <div className="space-y-2">
                  <Label>Filter by Month</Label>
                  <Select value={processMonth.toString()} onValueChange={v => { setProcessMonth(parseInt(v)); fetchPayslips(); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                        <SelectItem key={m} value={m.toString()}>{new Date(0, m-1).toLocaleString('default', { month: 'long' })}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" className="mt-8" onClick={fetchPayslips}>Refresh List</Button>
            </div>
            <div className="mb-3 flex justify-end">
              <TableExportActions
                rows={payslips}
                columns={payslipsExportColumns}
                fileName="payslips"
                title="Payslips"
              />
            </div>
            
            <Table className="text-hr-sm">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-hr-sm text-muted-foreground">Employee</TableHead>
                  <TableHead className="text-hr-sm text-muted-foreground">Month</TableHead>
                  <TableHead className="text-hr-sm text-muted-foreground">Gross</TableHead>
                  <TableHead className="text-hr-sm text-muted-foreground">Deductions</TableHead>
                  <TableHead className="text-hr-sm text-muted-foreground">Net Salary</TableHead>
                  <TableHead className="text-hr-sm text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="p-0">
                      <EmptyState
                        icon={ScrollText}
                        title="No payslips found"
                        description="No payslips available for the selected month. Process payroll to generate them."
                      />
                    </TableCell>
                  </TableRow>
                )}
                {payslips.map((payslip) => (
                  <TableRow key={payslip.id}>
                    <TableCell className="text-hr-sm">{payslip.employee_name} ({payslip.employee_id})</TableCell>
                    <TableCell className="text-hr-sm">{new Date(0, payslip.month-1).toLocaleString('default', { month: 'short' })} {payslip.year}</TableCell>
                    <TableCell className="text-hr-sm">{payslip.gross_earnings.toLocaleString()}</TableCell>
                    <TableCell className="text-hr-sm">{payslip.total_deductions.toLocaleString()}</TableCell>
                    <TableCell className="font-bold text-hr-sm">{payslip.net_salary.toLocaleString()}</TableCell>
                    <TableCell>
                       <Button size="sm" variant="outline" onClick={() => window.open(buildApiUrl(`/api/payslips/${payslip.id}/download`), '_blank')}>
                         <Download className="h-4 w-4 mr-2"/> PDF
                       </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PayrollManagement;
